/* ==========================================================================
   <tint-tabs> — accessible tab set with a no-JS fallback
   --------------------------------------------------------------------------
   Server-rendered markup is a stack of <section>s, each under its own <h3>.
   That is what a crawler and a JS-less browser get: everything visible, in
   order, correctly headed.

   This file upgrades it to a real ARIA tablist — roving tabindex, arrow-key
   navigation with Home/End, and aria-controls wiring. It adds the
   `is-ready` class that switches the CSS from stacked to tabbed, so the
   tabbed presentation can never appear without the behaviour behind it.
   ========================================================================== */

class TintTabs extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-tablist]');
    this.tabs = Array.from(this.querySelectorAll('[data-tab]'));
    this.panels = Array.from(this.querySelectorAll('[data-panel]'));
    if (!this.list || this.tabs.length < 2 || this.tabs.length !== this.panels.length) return;

    this.list.setAttribute('role', 'tablist');

    this.tabs.forEach((tab, i) => {
      const panel = this.panels[i];
      const id = panel.id || `tt-panel-${Math.random().toString(36).slice(2, 8)}`;
      panel.id = id;

      tab.id = `${id}-tab`;
      tab.setAttribute('role', 'tab');
      tab.setAttribute('aria-controls', id);
      tab.setAttribute('aria-selected', String(i === 0));
      tab.tabIndex = i === 0 ? 0 : -1;

      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      panel.tabIndex = 0;
      if (i !== 0) panel.hidden = true;

      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
    });

    this.index = 0;
    this.classList.add('is-ready');
  }

  onKeydown(event, i) {
    const last = this.tabs.length - 1;
    let next = null;
    switch (event.key) {
      case 'ArrowRight': next = i === last ? 0 : i + 1; break;
      case 'ArrowLeft':  next = i === 0 ? last : i - 1; break;
      case 'Home':       next = 0; break;
      case 'End':        next = last; break;
      default: return;
    }
    event.preventDefault();
    this.select(next);
    this.tabs[next].focus();
  }

  select(next) {
    if (next === this.index) return;
    this.tabs.forEach((tab, i) => {
      const on = i === next;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      this.panels[i].classList.toggle('is-active', on);
      this.panels[i].hidden = !on;
    });
    this.index = next;
  }
}

if (!customElements.get('tint-tabs')) {
  customElements.define('tint-tabs', TintTabs);
}
